/**
 * Company Combobox
 *
 * Server-backed, debounced company typeahead. The company list is paginated
 * (server default 20 per page), so a picker that loads "all" companies once
 * silently loses every company past page one — 96 on staging. This one asks
 * the server (`filter[search]`, matched case-insensitively across name,
 * display name, slug and registration number) on every pause in typing.
 *
 * The selected company's label is resolved by id (`GET /api/companies/:id`),
 * so it renders even when that company is not in the current results.
 */

import { useState } from 'react';
import { Check, ChevronsUpDown } from '@lognai/synapse-design-system-v2/icons';
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  cn,
} from '@lognai/synapse-design-system-v2';
import type { Company } from '@lognai/plugin-admin';
import { keepPreviousData } from '@tanstack/react-query';
import { useCompanies, useCompany } from '../../hooks/use-company-hooks';
import { useDebounce } from '../../hooks/use-debounce';

const PAGE_SIZE = 20;

export interface CompanyComboboxProps {
  value?: string;
  onChange: (companyId: string, company?: Company) => void;
  /** Hide companies that must not be offered (e.g. already granted). */
  isSelectable?: (company: Company) => boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Display name for any company id, resolved from the server. Falls back to
 * the id itself when the company cannot be read (deleted, or outside the
 * caller's visible set).
 */
export function CompanyName({ id, fallback }: { id: string; fallback?: string }) {
  const { data } = useCompany(id, { retry: false, staleTime: 5 * 60 * 1000 });
  return <>{data?.name ?? fallback ?? id}</>;
}

export function CompanyCombobox({
  value,
  onChange,
  isSelectable,
  placeholder = 'Select company',
  searchPlaceholder = 'Search companies…',
  emptyText = 'No companies found.',
  disabled = false,
  className,
}: CompanyComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const search = useDebounce(query.trim(), 300);

  const { data, isFetching } = useCompanies(
    { page: 1, limit: PAGE_SIZE, sortBy: 'name', sortOrder: 'asc', filter: search ? { search } : {} },
    { enabled: open, placeholderData: keepPreviousData, staleTime: 30 * 1000 }
  );
  const results = (data?.items ?? []).filter((company) => !isSelectable || isSelectable(company));

  const { data: selected } = useCompany(value ?? '', {
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  const selectedLabel = value ? (selected?.name ?? value) : undefined;
  const hasMore = (data?.pagination?.total ?? 0) > PAGE_SIZE;

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn('w-full justify-between font-normal', className)}
          disabled={disabled}
        >
          <span className={cn('truncate', !selectedLabel && 'text-muted-foreground')}>
            {selectedLabel ?? placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder={searchPlaceholder} value={query} onValueChange={setQuery} />
          <CommandList>
            {isFetching && results.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">Searching…</div>
            ) : (
              <CommandEmpty>{emptyText}</CommandEmpty>
            )}
            {results.length > 0 && (
              <CommandGroup>
                {results.map((company) => (
                  <CommandItem
                    key={company.id}
                    value={company.id}
                    onSelect={() => {
                      onChange(company.id, company);
                      setOpen(false);
                      setQuery('');
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4 shrink-0',
                        value === company.id ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <span className="truncate">{company.name}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {hasMore && (
              <div className="border-t px-3 py-2 text-xs text-muted-foreground">
                Showing the first {PAGE_SIZE} matches — keep typing to narrow down.
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
